import React, { useState, useEffect, useMemo } from 'react';
import { BOARD_SIZE } from '../../constants';
import { getPossibleMoves } from '../../utils/movement';
import player1Image from '../../assets/images/player_1.png';
import player2Image from '../../assets/images/player_2.png';

// Empty walls structure as in the game
const emptyWalls = {
  horizontal: Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE - 1).fill(false)),
  vertical: Array(BOARD_SIZE - 1).fill(null).map(() => Array(BOARD_SIZE).fill(false))
};

const CELL_SIZE = 32;
const ANIMATION_DURATION = 1000; // ms - slower movement
const PAUSE_DURATION = 1500; // ms - longer pause at target
const INITIAL_PAUSE = 2000; // ms - longer pause at initial position
const HINTS_ANIMATION_DURATION = 300; // ms - fade in/out duration for hints

function MovingStones() {
  // Board: only one black stone and some white stones for demo
  const initialBoard = useMemo(() => {
    const board = Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(null));
    board[3][3] = 'black';
    board[4][4] = 'white';
    board[3][4] = 'black';
    board[4][3] = 'white';
    return board;
  }, []);

  const [selectedStone] = useState({ row: 3, col: 3 });
  const [possibleMoves, setPossibleMoves] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showHints, setShowHints] = useState(true);

  // Calculate possible moves for the selected stone
  useEffect(() => {
    const moves = getPossibleMoves(initialBoard, emptyWalls, selectedStone.row, selectedStone.col);
    setPossibleMoves(moves);
  }, [selectedStone.row, selectedStone.col, initialBoard]);

  // Animation loop
  useEffect(() => {
    let timeoutId;

    const startAnimation = () => {
      // First hide hints
      setShowHints(false);

      // Wait for hints to fade out
      timeoutId = setTimeout(() => {
        // Move to target
        setIsAnimating(true);

        // After reaching target, pause
        timeoutId = setTimeout(() => {
          setIsAnimating(false);
          setShowHints(true);

          // Start next cycle after pause
          timeoutId = setTimeout(startAnimation, INITIAL_PAUSE);
        }, PAUSE_DURATION);
      }, HINTS_ANIMATION_DURATION);
    };

    // Start the animation loop
    timeoutId = setTimeout(startAnimation, INITIAL_PAUSE);

    // Cleanup
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="flex justify-center w-full">
      <div className="flex items-center justify-between w-full max-w-2xl">
        {/* Player 1 (Black) */}
        <div className="flex flex-col items-center opacity-0 sm:opacity-100">
          <img src={player1Image} alt="Player 1" className="w-24 h-24 object-contain" />
        </div>
        {/* Board */}
        <div className="relative rounded-lg p-4" style={{ backgroundColor: '#e1c083' }}>
          <div
            className="grid gap-0 relative"
            style={{
              gridTemplateColumns: `repeat(${BOARD_SIZE}, minmax(0, 1fr))`,
              width: `${BOARD_SIZE * CELL_SIZE}px`,
              height: `${BOARD_SIZE * CELL_SIZE}px`
            }}
          >
            {initialBoard.map((row, rowIndex) =>
              row.map((cell, colIndex) => {
                const isSelected = selectedStone.row === rowIndex && selectedStone.col === colIndex;
                const isPossibleMove = possibleMoves.some(move => move.row === rowIndex && move.col === colIndex);

                return (
                  <div
                    key={`${rowIndex}-${colIndex}`}
                    className="relative w-8 h-8"
                  >
                    {/* Grid lines */}
                    <div className="absolute inset-0 pointer-events-none">
                      {rowIndex < BOARD_SIZE - 1 && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#99804a]" />
                      )}
                      {colIndex < BOARD_SIZE - 1 && (
                        <div className="absolute right-0 top-0 bottom-0 w-0.5 bg-[#99804a]" />
                      )}
                    </div>
                    {/* Possible move indicator */}
                    {isPossibleMove && !cell && (
                      <div
                        className="absolute inset-2 rounded-full bg-blue-500/20"
                        style={{
                          opacity: showHints ? 1 : 0,
                          transition: `opacity ${HINTS_ANIMATION_DURATION}ms ease-in-out`,
                          transform: showHints ? 'scale(1)' : 'scale(0.8)',
                          transitionProperty: 'opacity, transform',
                          transitionDuration: `${HINTS_ANIMATION_DURATION}ms`,
                          transitionTimingFunction: 'ease-in-out'
                        }}
                      />
                    )}
                    {/* Stone */}
                    {cell && (
                      <div
                        className={`
                          absolute inset-2 rounded-full
                          ${cell === 'black'
                            ? 'bg-black shadow-sm shadow-white'
                            : 'bg-white shadow-sm shadow-black'
                          }
                          ${isSelected ? 'ring-1 ring-blue-400 ring-offset-2' : ''}
                        `}
                        style={{
                          transition: isSelected ? `transform ${ANIMATION_DURATION}ms cubic-bezier(.2,1,.6,1)` : 'none',
                          transform: isSelected && isAnimating ? 'translateX(-65px)' : 'none'
                        }}
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
        {/* Player 2 (White) */}
        <div className="flex flex-col items-center opacity-0 sm:opacity-100">
          <img src={player2Image} alt="Player 2" className="w-24 h-24 object-contain opacity-30" />
        </div>
      </div>
    </div>
  );
}

export default MovingStones; 
